import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { posToDOMRect } from '@tiptap/core'
import useFloatingPosition from './useFloatingPosition'
import { getPortalContainer } from './portal'
import { streamWrite } from './aiApi'
import { fragmentHtml, stripFences, surroundingText } from './aiContent'
import { acceptChanges, getChange, newChangeId, rejectChanges, setChanges } from './aiReview'
import useStyles from './styles'


const editActions = [
  { group: 'Edit', title: 'Improve writing', instruction: 'Improve the writing: clearer, better flow, same meaning.' },
  { group: 'Edit', title: 'Fix spelling & grammar', instruction: 'Fix spelling, grammar and punctuation. Change nothing else.' },
  { group: 'Edit', title: 'Make shorter', instruction: 'Make it shorter while keeping all the important points.' },
  { group: 'Edit', title: 'Make longer', instruction: 'Expand it with more detail and explanation, without inventing facts.' },
  { group: 'Edit', title: 'Simplify language', instruction: 'Rewrite it in plain language a non-lawyer client understands.' },
  { group: 'Edit', title: 'Make more formal', instruction: 'Make the tone more formal and professional.' },
  { group: 'Edit', title: 'Turn into bullet points', instruction: 'Turn it into a bulleted list of the key points.' },
  { group: 'Edit', title: 'Summarize', instruction: 'Replace it with a concise summary.' },
  ...['English', 'Bulgarian', 'German', 'French'].map(language => ({
    group: 'Translate',
    title: `Translate to ${language}`,
    instruction: `Translate it to ${language}. Keep the formatting.`
  }))
]

const insertActions = [
  { group: 'Write', title: 'Continue writing', instruction: 'Continue writing from where the text before the cursor ends.' },
  { group: 'Write', title: 'Summarize the text above', instruction: 'Write a short summary of the text before the cursor.' },
  { group: 'Write', title: 'Write a conclusion', instruction: 'Write a short conclusion for the text before the cursor.' },
  { group: 'Draft', title: 'Draft an outline', instruction: 'Draft an outline, as headings and bullet points, for the document so far.' },
  { group: 'Draft', title: 'Draft a client email', instruction: 'Draft a short email to the client that explains the text before the cursor.' }
]

// Rect of the change's proposed content, falling back to the target range
const referenceRect = (editor, range, changeId) => {
  const proposal = changeId && editor.view.dom.querySelector(`[data-ai-change="${changeId}"]`)
  if (proposal) {
    return proposal.getBoundingClientRect()
  }
  return posToDOMRect(editor.view, range.from, range.to)
}

// Notion-style "Ask AI" popover. Writes at the cursor or rewrites the
// selection; the result streams into the document as a proposed change
// that is accepted, discarded or refined from here.
const AiPrompt = ({ editor, range, onClose }) => {
  const localClasses = useStyles()
  const popoverRef = useRef(null)
  const inputRef = useRef(null)
  const abortRef = useRef(null)
  const changeIdRef = useRef(null)
  const [phase, setPhase] = useState('input') // input | streaming | review | error
  const [instruction, setInstruction] = useState('')
  const [lastRun, setLastRun] = useState(null)
  const [error, setError] = useState('')
  const [activity, setActivity] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const [length, setLength] = useState(0)
  const editsSelection = range.to > range.from && editor.state.doc.textBetween(range.from, range.to, ' ').trim() !== ''
  const reviewing = phase === 'review'
  const actions = (editsSelection || reviewing ? editActions : insertActions)
    .filter(action => action.title.toLowerCase().includes(instruction.trim().toLowerCase()))

  const currentChange = () => changeIdRef.current && getChange(editor.view, changeIdRef.current)

  useFloatingPosition(
    popoverRef,
    () => ({
      getBoundingClientRect: () => {
        const change = currentChange()
        return referenceRect(editor, change || range, change && change.id)
      },
      contextElement: editor.view.dom
    }),
    { open: true, placement: 'bottom-start', gap: 8 },
    [phase, length]
  )

  // Streams `body` into the change `target` ({ id, from, to }). A failed
  // run restores the proposal that was there before, if any.
  const run = (body, target) => {
    const previous = currentChange()
    changeIdRef.current = target.id
    const controller = new AbortController()
    abortRef.current = controller
    setLastRun({ body, target })
    setInstruction('')
    setActiveIndex(-1)
    setError('')
    setActivity('')
    setPhase('streaming')

    let html = ''
    let streamError = ''
    let frame = 0
    const show = (status) => {
      cancelAnimationFrame(frame)
      frame = 0
      setChanges(editor.view, [{ ...target, html, status, controls: false }])
      setLength(html.length)
    }
    streamWrite(body, (event) => {
      if (event.type === 'text') {
        html += event.text
        setActivity('')
        if (!frame) {
          frame = requestAnimationFrame(() => show('streaming'))
        }
      } else if (event.type === 'reset') {
        // Text written before a tool call isn't content
        html = ''
        show('streaming')
      } else if (event.type === 'activity') {
        setActivity(event.label)
      } else if (event.type === 'error') {
        streamError = event.message
      }
    }, controller.signal)
      .then(() => {
        if (streamError || !stripFences(html).trim()) {
          throw new Error(streamError || 'The AI returned nothing. Try rephrasing.')
        }
        show('ready')
        setPhase('review')
      })
      .catch((reason) => {
        cancelAnimationFrame(frame)
        if (controller.signal.aborted) {
          return
        }
        if (previous) {
          setChanges(editor.view, [previous])
        } else {
          rejectChanges(editor.view, [target.id])
          changeIdRef.current = null
        }
        setError(reason.message)
        setPhase('error')
      })
      .then(() => {
        abortRef.current = null
      })
  }

  // While reviewing, a new instruction refines the proposal; otherwise it
  // works on the selection or writes at the cursor
  const submit = (action) => {
    const text = action ? action.instruction : instruction.trim()
    if (!text) {
      return
    }
    const { doc } = editor.state
    const previous = currentChange()
    if (previous && reviewing) {
      run({ instruction: text, selection: stripFences(previous.html) }, previous)
      return
    }
    const target = previous || { id: newChangeId(), ...range }
    const body = editsSelection ?
      { instruction: text, selection: fragmentHtml(editor, doc.slice(target.from, target.to).content) } :
      { instruction: text, ...surroundingText(doc, target.from, target.to) }
    run(body, target)
  }

  const retry = () => {
    const previous = currentChange()
    run(lastRun.body, previous ? { ...lastRun.target, from: previous.from, to: previous.to } : lastRun.target)
  }

  const accept = () => {
    acceptChanges(editor.view, [changeIdRef.current])
    changeIdRef.current = null
    onClose()
  }

  const discard = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    if (changeIdRef.current) {
      rejectChanges(editor.view, [changeIdRef.current])
      changeIdRef.current = null
    }
    onClose()
    editor.commands.focus()
  }, [editor, onClose])

  // Clicking elsewhere closes the prompt, leaving a finished proposal in the
  // document with its own Accept / Reject buttons
  useEffect(() => {
    const handleMouseDown = (event) => {
      if (popoverRef.current && popoverRef.current.contains(event.target)) {
        return
      }
      const change = changeIdRef.current && getChange(editor.view, changeIdRef.current)
      if (change && change.status === 'ready') {
        setChanges(editor.view, [{ id: change.id, controls: true }])
        changeIdRef.current = null
        onClose()
      } else {
        discard()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [editor, discard, onClose])

  // Stop a running request when the editor goes away
  useEffect(() => () => abortRef.current && abortRef.current.abort(), [])

  useEffect(() => {
    if (phase !== 'streaming' && inputRef.current) {
      inputRef.current.focus()
    }
  }, [phase])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      discard()
    } else if (event.key === 'ArrowDown' && actions.length) {
      event.preventDefault()
      setActiveIndex((activeIndex + 1) % actions.length)
    } else if (event.key === 'ArrowUp' && actions.length) {
      event.preventDefault()
      setActiveIndex((activeIndex + actions.length - 1) % actions.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (activeIndex >= 0 && actions[activeIndex]) {
        submit(actions[activeIndex])
      } else if (reviewing && !instruction.trim()) {
        accept()
      } else {
        submit()
      }
    }
  }

  const keepFocus = event => event.preventDefault()
  const showActions = phase === 'input' || (reviewing && instruction.trim())

  return ReactDOM.createPortal(
    <div className={`${localClasses.popover} ${localClasses.aiPrompt}`} ref={popoverRef}>
      {
        phase === 'streaming' ?
        <div className={localClasses.aiPromptRow}>
          <span className={localClasses.aiSparkle}>✦</span>
          <span className={localClasses.aiStatus}>{activity || 'AI is writing…'}</span>
          <button type='button' className={localClasses.menuButton} onMouseDown={keepFocus} onClick={discard}>
            Stop
          </button>
        </div> :
        <div className={localClasses.aiPromptRow}>
          <span className={localClasses.aiSparkle}>✦</span>
          <input
            ref={inputRef}
            className={localClasses.aiPromptInput}
            placeholder={
              reviewing ? 'Tell AI what to change…' :
              (editsSelection ? 'Ask AI to edit the selection…' : 'Ask AI to write anything…')
            }
            value={instruction}
            onChange={(event) => {
              setInstruction(event.target.value)
              setActiveIndex(-1)
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type='button'
            className={localClasses.menuButton}
            onMouseDown={keepFocus}
            disabled={!instruction.trim()}
            onClick={() => submit()}
            title='Send (Enter)'
          >
            ↵
          </button>
        </div>
      }
      {
        phase === 'error' &&
        <div className={localClasses.aiPromptFooter}>
          <span className={localClasses.mediaError}>{error || 'Something went wrong.'}</span>
          {
            lastRun &&
            <button type='button' className={localClasses.menuButton} onMouseDown={keepFocus} onClick={retry}>
              Try again
            </button>
          }
        </div>
      }
      {
        reviewing &&
        <div className={localClasses.aiPromptFooter}>
          <button type='button' className={`${localClasses.menuButton} primary`} onMouseDown={keepFocus} onClick={accept}>
            Accept
          </button>
          <button type='button' className={localClasses.menuButton} onMouseDown={keepFocus} onClick={discard}>
            Discard
          </button>
          <button
            type='button'
            className={localClasses.menuButton}
            onMouseDown={keepFocus}
            onClick={retry}
          >
            Try again
          </button>
        </div>
      }
      {
        showActions && actions.length > 0 &&
        <div className={localClasses.aiActions}>
          {
            actions.map((action, index) => (
              <React.Fragment key={action.title}>
                {
                  (index === 0 || actions[index - 1].group !== action.group) &&
                  <div className={localClasses.menuLabel}>{action.group}</div>
                }
                <div
                  className={localClasses.slashItem}
                  data-active={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={keepFocus}
                  onClick={() => submit(action)}
                >
                  <div className={localClasses.slashTitle}>{action.title}</div>
                </div>
              </React.Fragment>
            ))
          }
        </div>
      }
    </div>,
    getPortalContainer(editor)
  )
}

export default AiPrompt
